/**
 * Docs Resources
 *
 * Provides access to documentation components package catalog including
 * components, utilities, types, and markdown extensions.
 */

import { McpServer } from '@modelcontextprotocol/sdk/server/mcp.js';
import docsCatalog from '../data/docs-catalog.json' with { type: 'json' };

interface DocsComponent {
  name: string;
  description: string;
  category: string;
  props?: unknown[];
  slots?: unknown[];
  features?: string[];
  example?: string;
  peerDependency?: string;
}

interface DocsCatalog {
  version: string;
  packageName: string;
  description: string;
  exports: Record<string, { importPath: string; description: string }>;
  components: DocsComponent[];
  utilities: Record<string, unknown>;
  types: unknown[];
  markdownExtensions: Record<string, unknown>;
  styles: Record<string, unknown>;
  peerDependencies: Record<string, string>;
  dependencies: Record<string, string>;
}

const docs = docsCatalog as DocsCatalog;

/**
 * Register docs-related resources
 */
export function registerDocsResources(server: McpServer): void {
  // All docs package information
  server.resource('docs://all', 'docs://all', async (uri) => ({
    contents: [
      {
        uri: uri.href,
        mimeType: 'application/json',
        text: JSON.stringify(docs, null, 2),
      },
    ],
  }));

  // Components overview
  server.resource('docs://components', 'docs://components', async (uri) => ({
    contents: [
      {
        uri: uri.href,
        mimeType: 'application/json',
        text: JSON.stringify(
          {
            packageName: docs.packageName,
            description: 'Svelte 5 components for markdown documentation',
            components: docs.components,
          },
          null,
          2
        ),
      },
    ],
  }));

  // Utilities overview
  server.resource('docs://utilities', 'docs://utilities', async (uri) => ({
    contents: [
      {
        uri: uri.href,
        mimeType: 'application/json',
        text: JSON.stringify(
          {
            packageName: docs.packageName,
            utilities: docs.utilities,
          },
          null,
          2
        ),
      },
    ],
  }));

  // Types reference
  server.resource('docs://types', 'docs://types', async (uri) => ({
    contents: [
      {
        uri: uri.href,
        mimeType: 'application/json',
        text: JSON.stringify(
          {
            packageName: docs.packageName,
            types: docs.types,
          },
          null,
          2
        ),
      },
    ],
  }));

  // Markdown extensions reference
  server.resource('docs://extensions', 'docs://extensions', async (uri) => ({
    contents: [
      {
        uri: uri.href,
        mimeType: 'application/json',
        text: JSON.stringify(
          {
            packageName: docs.packageName,
            markdownExtensions: docs.markdownExtensions,
            extensionFunctions: docs.utilities.extensions,
          },
          null,
          2
        ),
      },
    ],
  }));

  // Styles reference
  server.resource('docs://styles', 'docs://styles', async (uri) => ({
    contents: [
      {
        uri: uri.href,
        mimeType: 'application/json',
        text: JSON.stringify(
          {
            packageName: docs.packageName,
            styles: docs.styles,
          },
          null,
          2
        ),
      },
    ],
  }));

  // Quick reference guide
  server.resource('docs://quick-reference', 'docs://quick-reference', async (uri) => ({
    contents: [
      {
        uri: uri.href,
        mimeType: 'application/json',
        text: JSON.stringify(
          {
            packageName: docs.packageName,
            description: docs.description,
            exports: docs.exports,
            componentList: docs.components.map((c) => ({
              name: c.name,
              description: c.description,
              category: c.category,
            })),
            parserFunctions: ['parseMarkdown', 'extractToc'],
            highlighterFunctions: ['highlightCode', 'getHighlighter', 'escapeHtml'],
            extensionFunctions: [
              'admonitionExtension',
              'footnoteExtension',
              'definitionListExtension',
              'mermaidExtension',
              'getAllExtensions',
              'resetFootnoteStore',
              'renderFootnotes',
            ],
            helpers: ['cn', 'tv', 'VariantProps'],
            peerDependencies: docs.peerDependencies,
          },
          null,
          2
        ),
      },
    ],
  }));
}
